/**
 * Ad refresh on the page, for ONE filled GPT slot (Atlas ch.8, ADR-033).
 *
 * `RefreshController` decides WHETHER a refresh may fire (R1-R9) from the
 * facts it is told. This class is what tells it: it watches the slot element
 * with an `IntersectionObserver` (R1/R8: local viewport state, never the
 * consent-gated beacon pipeline), the tab's visibility (R1/R3) and the
 * reader's pointer/keyboard/scroll/touch activity (R4), and arms ONE timer
 * for exactly the in-view time still owed. Nothing polls: the timer exists
 * only while the slot is eligible, and every state change re-arms it.
 *
 * `refresh()` is the full auction (R6) and is the caller's: it resolves once
 * GAM has answered the refreshed slot and rejects when the refresh could not
 * be completed. The controller never starts a second one while it is pending
 * (R9).
 *
 * `stop()` releases the observer, the listeners and the timer, and is
 * idempotent. Nothing here throws into the page.
 */
import { RefreshController } from './RefreshController.js';

/** What counts as the reader still being there (R4). */
export const INTERACTION_EVENTS = Object.freeze([
  'pointerdown',
  'pointermove',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
]);

/** After a refresh that could not complete (no GAM answer, GPT error), how
 *  long before trying again. The dwell clock is NOT reset on a failure, so
 *  without this wait a failing slot would retry back to back. */
export const REFRESH_RETRY_MS = 30_000;

/** IntersectionObserver thresholds: both MRC ratios the controller may need
 *  (30% large format, 50% otherwise), plus fully out and fully in. */
const THRESHOLDS = [0, 0.3, 0.5, 1];

const swallow = (fn, onError) => {
  try {
    return fn();
  } catch (err) {
    onError?.(err);
    return undefined;
  }
};

export class SlotRefresher {
  #ctl;
  #el;
  #win;
  #doc;
  #setTimeoutFn;
  #clearTimeoutFn;
  #onDiagnostic;
  #now;
  #retryAt = 0;
  #timer = null;
  #observer = null;
  #parkedIdle = false;
  #started = false;
  #stopped = false;
  #onVisibilityChange = () =>
    this.#guard(() => {
      this.#ctl.onTabVisibility(!this.#doc?.hidden);
      this.#schedule();
    });
  #onInteraction = () =>
    this.#guard(() => {
      this.#ctl.onInteraction();
      if (this.#parkedIdle) {
        this.#parkedIdle = false;
        this.#schedule();
      }
    });

  /**
   * @param {object} opts
   * @param {Element} opts.element             The filled slot's element.
   * @param {object} opts.config               RefreshController config ({enabled, intervalMs, ...}); clamped there.
   * @param {number} [opts.areaPx]             Served creative area, for the MRC ratio.
   * @param {() => Promise<unknown>} opts.refresh  One full auction + GPT refresh for this slot only.
   * @param {Window} [opts.win]
   * @param {() => number} [opts.now]
   * @param {(fn: Function, ms: number) => unknown} [opts.setTimeoutFn]
   * @param {(id: unknown) => void} [opts.clearTimeoutFn]
   * @param {(msg: string, detail?: unknown) => void} [opts.onDiagnostic]
   */
  constructor({
    element,
    config,
    areaPx = 0,
    refresh,
    win = typeof window !== 'undefined' ? window : undefined,
    now = () => Date.now(),
    setTimeoutFn,
    clearTimeoutFn,
    onDiagnostic,
  }) {
    this.#el = element;
    this.#win = win;
    this.#doc = win?.document;
    // Wrapped, never stored bare: a host timer called as a method throws
    // "Illegal invocation" in a real browser (see AuctionDriver).
    this.#setTimeoutFn = setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
    this.#clearTimeoutFn = clearTimeoutFn ?? ((id) => clearTimeout(id));
    this.#onDiagnostic = onDiagnostic ?? (() => {});
    this.#now = now;
    this.#ctl = new RefreshController({ config, areaPx, now, onRefresh: () => refresh() });
  }

  /** Refreshes completed so far (diagnostics and tests). */
  get count() {
    return this.#ctl.count;
  }

  /** Why the next refresh is not firing right now, or null. */
  blockedReason() {
    if (this.#stopped) return 'stopped';
    return this.#ctl.blockedReason();
  }

  /**
   * Begin watching. Returns false (and watches nothing) when refresh is off,
   * or when this browser cannot tell whether the slot is in view: without
   * `IntersectionObserver` R1 cannot be proven, so the slot never refreshes.
   */
  start() {
    if (this.#started || this.#stopped) return false;
    this.#started = true;
    if (!this.#ctl.cfg.enabled || this.#ctl.cfg.maxPerPageview === 0) return false;
    const IO = this.#win?.IntersectionObserver;
    if (typeof IO !== 'function' || !this.#el) return false;
    return (
      swallow(
        () => {
          this.#observer = new IO(
            ([entry]) =>
              this.#guard(() => {
                this.#ctl.onVisibility(entry?.intersectionRatio ?? 0);
                this.#schedule();
              }),
            { threshold: THRESHOLDS },
          );
          this.#observer.observe(this.#el);
          this.#ctl.onTabVisibility(!this.#doc?.hidden);
          this.#doc?.addEventListener('visibilitychange', this.#onVisibilityChange);
          for (const ev of INTERACTION_EVENTS) {
            this.#win.addEventListener(ev, this.#onInteraction, { passive: true, capture: true });
          }
          return true;
        },
        (e) => {
          this.#onDiagnostic('refresh not armed', String(e));
          this.stop();
        },
      ) ?? false
    );
  }

  /** Release everything. Safe to call more than once, and before start(). */
  stop() {
    if (this.#stopped) return;
    this.#stopped = true;
    this.#clearTimer();
    swallow(() => this.#observer?.disconnect());
    this.#observer = null;
    swallow(() => this.#doc?.removeEventListener('visibilitychange', this.#onVisibilityChange));
    for (const ev of INTERACTION_EVENTS) {
      swallow(() => this.#win?.removeEventListener(ev, this.#onInteraction, { capture: true }));
    }
  }

  #guard(fn) {
    if (this.#stopped) return;
    swallow(fn, (e) => this.#onDiagnostic('refresh error', String(e)));
  }

  #clearTimer() {
    if (this.#timer !== null) swallow(() => this.#clearTimeoutFn(this.#timer));
    this.#timer = null;
  }

  /** One timer, for exactly the in-view time still owed, and only while the
   *  slot is in view on a visible tab. Off-screen or hidden, no timer runs:
   *  the next observer or visibility event re-arms it (R3). */
  #schedule() {
    this.#clearTimer();
    if (this.#stopped || this.#ctl.inFlight || !this.#ctl.eligible) return;
    // A failed refresh keeps its dwell, so it would be due again at once;
    // REFRESH_RETRY_MS holds it back however often the slot re-enters view.
    const delayMs = Math.max(this.#ctl.remainingDwellMs(), this.#retryAt - this.#now());
    this.#timer = this.#setTimeoutFn(() => {
      this.#timer = null;
      this.#tick().catch((e) => this.#onDiagnostic('refresh error', String(e)));
    }, delayMs);
  }

  async #tick() {
    if (this.#stopped) return;
    const result = await this.#ctl.tick();
    if (this.#stopped) return;
    if (result.refreshed) {
      this.#retryAt = 0;
      if (this.#ctl.count >= this.#ctl.cfg.maxPerPageview)
        this.stop(); // R5
      else this.#schedule();
      return;
    }
    switch (result.reason) {
      case 'idle':
        // R4: wait for the reader. The next interaction re-arms the timer.
        this.#parkedIdle = true;
        break;
      case 'cap reached':
      case 'disabled':
        this.stop();
        break;
      case 'refresh failed':
        this.#onDiagnostic('refresh failed', String(result.error));
        this.#retryAt = this.#now() + REFRESH_RETRY_MS;
        this.#schedule();
        break;
      case 'dwell not met':
        this.#schedule();
        break;
      default:
        // Not in view / tab hidden / in flight: an event re-arms it.
        break;
    }
  }
}
